// 待宵の花嫁「ヴェールの招待」(veilInvitation・床の帯 kind 'veil') の見た目 — 両側から押し寄せるヴェールの津波 (2026-10-23・比較版 ?brideVeilFx=sides)。
//
// 作者の原文 (10-23・手描きの図つき): 「両サイドから津波のように押し寄せてくるような感じでベールは書いてほしいのね。
//   今のだと下から生えてきているような感じになっているから、これをうまく表現できる?」
//   (hands-veil へ) 「動きはいい感じなんだけど、なんか今のだと本当に布が生えてきてるみたいになって」
// 図: 主人公の後ろと花嫁の後ろに、人の2倍ほどの背の波が1つずつ。波頭は内側 (2人のいる中央) へ巻いて覆いかぶさる。
// だから「下から上へ伸ばす」動きを使わない:
//   ① 予告 (850ms): 波は最初から背が高いまま (0.86)、帯の外 (240px) から横へ寄せてくる。寄りながら波頭が内へ傾いていく。
//   ② 当たり 0〜150ms: 波頭が帯の上へ崩れ落ちる (巻きが深くなり、頂が下がる)。床に菫の照り (弾けない合図)。
//   ③ 当たりの間: 崩れたまま、波頭と裾がはためく。
//   ④ 当たりの後: 重力で崩れ落ちて床に広がりながら薄れる (頂が落ちる・横は細らない)。
// 布の絵 = ヴェールの幕の絵 (bride_veil_curtain.png・10-23 wall-cloth と同じ物) を、波の形に曲げた網目に貼る。
//   絵の下 (床の裾) を波の根元、絵の上の裂けた先を波頭の先に置く＝裂けた先がしぶきになる。
//   網目の継ぎ目が二重に濃くならないよう、1枚ずつ脇の画面へ 'copy' で描いてから重ねる。
// 貼る絵の2つ (10-23 作者「動き的にはいい感じになっているから、これをヴェールの布の絵を使ってやってほしい」・動きは同じ):
//   sides       = ヴェールの幕の絵 (上のとおり・細い尖りが並ぶ)。
//   sides-tulle = 透けるヴェールの大きな一枚布の絵 (bride_veil_tulle.png・10-23 Codex) を、片側の波ごとに3枚まるごと重ねる。
//     作者「今までの絵の布っていうのは、ちぎった布がいっぱい出てきているような感じになっているから、2、3枚のでかい布を使ってほしい」。
// 当たりは変えない (src/attacks.ts の2つの帯)。波の根元は帯の外側の縁、巻いた先は帯の内側の縁 (隙間の手前) まで。
//   2つの帯の間の隙間 (前へ1回転がって入る所) の床際には布を置かない。描くだけ。
import type { ActiveHit } from './types'
import { hash01 } from './fx_kit'
import { sw as swc } from './purple_swap'
import { brideVeilCurtain } from './bride_veil_wallcloth'

//   sides-wave  = 絵を貼らず、コードで平らな影絵の波を描く (10-23 検収 V-1「中にいっぱい線が見えるのは何?…脊椎みたいなのが」・S-1「波に見えない」)。
//     線の正体＝一枚布の絵 bride_veil_tulle.png の斜めの折り目の縞が、背の高い網目で曲げられ3枚ずらして重なった物。だから中に線を描かない。
//     波に見えない訳＝背の高い帯が上だけ巻く形は「立った幕」。波は背が低く外へ長く引いた胴・内へ窪んだ顔・覆いかぶさる波頭で読める
//     (北斎の神奈川沖浪裏・アニメの水の FX＝暗い胴＋明るい波頭＋崩れた所の千切れ)。記録 docs/research/2026-10-23-bride-veil-sides-wave.md。
export const VEIL_SIDES_KINDS = ['sides', 'sides-tulle', 'sides-wave'] as const
export type VeilSidesKind = typeof VEIL_SIDES_KINDS[number]
export function isVeilSidesKind(v: string): v is VeilSidesKind { return (VEIL_SIDES_KINDS as readonly string[]).includes(v) }

const TULLE_SRC = '/sprites/fx/hazard/bride_veil_tulle.png'
let tulleImg: HTMLImageElement | null = null
let tulleLoading = false
export function preloadBrideVeilTulle(): void {
  if (tulleImg || tulleLoading || typeof Image === 'undefined') return
  tulleLoading = true
  const img = new Image()
  img.onload = () => { tulleImg = img }
  img.onerror = () => { tulleLoading = false }
  img.src = TULLE_SRC
}
export function setBrideVeilTulleForTest(img: HTMLImageElement | null): void { tulleImg = img }

// 絵の取り出し方。w/h = 絵の大きさ・slice = 横に長い絵から切る幅 (高さの倍率・波ごと層ごとにずらす。0 = 絵をまるごと)・
// cut = 上 (波頭の先) を使わない割合・layers = 重ねる布 (奥→手前)。
type Skin = { img: CanvasImageSource; w: number; h: number; slice: number; cut: number; layers: readonly Layer[] }
function skinOf(kind: VeilSidesKind): Skin | null {
  if (kind === 'sides-wave') return null
  if (kind === 'sides-tulle') {
    if (!tulleImg) preloadBrideVeilTulle()
    const img = tulleImg
    return img ? { img, w: img.naturalWidth || img.width, h: img.naturalHeight || img.height, slice: 0, cut: 0, layers: TULLE_LAYERS } : null
  }
  const img = brideVeilCurtain()
  // 縦は裂けた先の長い所を少し落とす (先が爪のように尖って見えないよう・上 14% を使わない)。
  return img ? { img, w: img.naturalWidth || img.width, h: img.naturalHeight || img.height, slice: 0.95, cut: 0.14, layers: LAYERS } : null
}

function clamp01(v: number): number { return v < 0 ? 0 : v > 1 ? 1 : v }
function smooth01(a: number, b: number, v: number): number {
  const p = clamp01((v - a) / (b - a))
  return p * p * (3 - 2 * p)
}
function easeOut(p: number): number { const q = 1 - clamp01(p); return 1 - q * q * q }
function easeOutBack(p: number): number {
  const x = clamp01(p) - 1
  const c = 1.4
  return 1 + (c + 1) * x * x * x + c * x * x
}
function lerp(a: number, b: number, t: number): number { return a + (b - a) * t }

const APPROACH_PX = 240   // 予告の始めに波がいる所 (帯の外へ)
const CRASH_MS = 150      // 当たりの 0ms から波頭が崩れ落ちるまで
const FALL_MS = 650       // 当たりの後、崩れ落ちて消えるまで
const GAP_FLOOR_PX = 60   // この高さより下は隙間へはみ出さない
const OVERHANG_PX = 40    // 巻いた先が帯の内側の縁を越えてよい量
const NV = 26             // 網目の縦 (根元→波頭の先)。少ないと巻いた背中が角ばって見える
const NS = 2              // 網目の横 (背中→腹)。幅の向きはまっすぐなので2つで足りる (多いと重い)

export function veilSidesLingerMs(): number { return FALL_MS }

type Geom = { id: number; xStart: number; xEnd: number; w: number; cx: number; dir: 1 | -1; seed: number; H: number; src: number }

/** 波の姿勢。slide=外へずれた量 (px)・hs=高さの倍率・curl=波頭の曲がり (ラジアン)・spread=床に広がる倍率・alpha。 */
export type SidesPose = { slide: number; hs: number; curl: number; spread: number; alpha: number }

const dirs = new Map<number, 1 | -1>()
function geomOf(hz: ActiveHit, dir: 1 | -1): Geom {
  const w = hz.xEnd - hz.xStart
  const seed = hz.id * 2.17 + 11.3
  return { id: hz.id, xStart: hz.xStart, xEnd: hz.xEnd, w, cx: (hz.xStart + hz.xEnd) / 2, dir, seed,
    H: Math.max(215, Math.min(270, 170 + w * 0.35)), src: hash01(seed) }
}

type V2 = { u: number; y: number }   // u = 帯の外側の縁から内へ +・y = 床から上へ +

/**
 * 網目の点 (NV+1 行 × NS+1 列)。行 0 = 床の根元、行 NV = 波頭の先。列 0 = 背中 (外)、列 NS = 腹 (内)。
 * 帯の座標のまま返す (テストで隙間へのはみ出しを測る)。
 */
export function veilSidesMesh(w: number, H: number, pose: SidesPose, timeSec: number, seed: number, lag = 0): V2[][] {
  const t = timeSec - lag
  const L = H * 1.32 * pose.hs
  // 背骨: 根元は少し後ろへ反り、上 4 割で内へ巻く。曲がりは先ほど強い。
  const sp: Array<{ u: number; y: number; nu: number; ny: number }> = []
  let u = 0, y = 0
  for (let i = 0; i <= NV; i += 1) {
    const s = i / NV
    const k = Math.pow(smooth01(0.32, 1, s), 1.25)
    const wob = Math.sin(t * 2.6 + seed + s * 3.2) * 0.12 * s
    const phi = -0.14 + pose.curl * k + wob
    if (i > 0) { u += Math.sin(phi) * L / NV; y += Math.cos(phi) * L / NV }
    sp.push({ u, y, nu: Math.cos(phi), ny: -Math.sin(phi) })
  }
  // 幅: 根元が太く、先へ細る。床に広がる時は根元の幅が増す。
  const half = (s: number): number => (H * 0.34 * Math.pow(1 - s, 0.85) + H * 0.035) * lerp(1, pose.spread, 1 - s)
  const rows: V2[][] = []
  for (let i = 0; i <= NV; i += 1) {
    const s = i / NV, p = sp[i]!, hw = half(s)
    const row: V2[] = []
    for (let j = 0; j <= NS; j += 1) {
      const c = (j / NS) * 2 - 1
      const flap = Math.sin(t * 3.4 + seed * 1.7 + s * 5 + c * 1.3) * H * 0.018 * s
      row.push({ u: p.u + p.nu * hw * c + flap, y: p.y + p.ny * hw * c })
    }
    rows.push(row)
  }
  // 根元の行は床に置く (背中が帯の外側の縁)。
  const base = rows[0]![0]!.u
  for (const row of rows) for (const q of row) { q.u -= base; q.y = Math.max(0, q.y) }
  // 内への届き: 巻いた先は帯の内側の縁 + OVERHANG_PX まで・床際 (GAP_FLOOR_PX 未満) は内側の縁まで。
  let scale = 1
  for (const row of rows) for (const q of row) {
    const lim = q.y < GAP_FLOOR_PX ? w : w + OVERHANG_PX
    if (q.u > lim && q.u > 1) scale = Math.min(scale, lim / q.u)
  }
  for (const row of rows) for (const q of row) { q.u = q.u * scale - pose.slide }
  return rows
}

/** 当たりの開始からの ms での形 (テストと残りの描画で使う)。ms < 0 は予告。 */
export function veilSidesPose(ms: number, activeMs: number, telegraphMs = 850): SidesPose {
  if (ms < 0) {
    const p = clamp01(1 + ms / telegraphMs)
    return {
      slide: APPROACH_PX * (1 - easeOut(p / 0.88)),
      hs: 0.86 + 0.14 * smooth01(0, 0.9, p),
      curl: 0.5 + 1.5 * smooth01(0.1, 1, p),
      spread: 1,
      alpha: smooth01(0, 0.28, p) * (0.75 + 0.25 * p),
    }
  }
  const crash = easeOutBack(ms / CRASH_MS)
  const after = ms - activeMs
  const fall = after > 0 ? clamp01(after / FALL_MS) : 0
  const g = fall * fall
  return {
    slide: 0,
    hs: lerp(1, 0.8, crash) * (1 - 0.72 * g),
    curl: 2.0 + 1.05 * crash + 0.12 * smooth01(CRASH_MS, activeMs, ms) + 0.45 * g,
    spread: 1 + 0.55 * g,
    alpha: after > FALL_MS ? 0 : 1 - smooth01(0.25, 1, fall),
  }
}

// ---------------------------------------------------------------- 描く

// 三角形1つに絵を貼る (アフィン)。clip を 0.6px 広げて継ぎ目を埋める。
function texTri(
  c: CanvasRenderingContext2D, img: CanvasImageSource,
  s0x: number, s0y: number, s1x: number, s1y: number, s2x: number, s2y: number,
  d0x: number, d0y: number, d1x: number, d1y: number, d2x: number, d2y: number,
): void {
  const det = (s1x - s0x) * (s2y - s0y) - (s2x - s0x) * (s1y - s0y)
  if (Math.abs(det) < 1e-6) return
  const a = ((d1x - d0x) * (s2y - s0y) - (d2x - d0x) * (s1y - s0y)) / det
  const b = ((d1y - d0y) * (s2y - s0y) - (d2y - d0y) * (s1y - s0y)) / det
  const cc = ((d2x - d0x) * (s1x - s0x) - (d1x - d0x) * (s2x - s0x)) / det
  const d = ((d2y - d0y) * (s1x - s0x) - (d1y - d0y) * (s2x - s0x)) / det
  const e = d0x - a * s0x - cc * s0y
  const f = d0y - b * s0x - d * s0y
  const mx = (d0x + d1x + d2x) / 3, my = (d0y + d1y + d2y) / 3
  const grow = (x: number, y: number): [number, number] => {
    const l = Math.hypot(x - mx, y - my) || 1
    return [x + (x - mx) / l * 0.6, y + (y - my) / l * 0.6]
  }
  c.save()
  c.beginPath()
  c.moveTo(...grow(d0x, d0y)); c.lineTo(...grow(d1x, d1y)); c.lineTo(...grow(d2x, d2y))
  c.closePath()
  c.clip()
  c.transform(a, b, cc, d, e, f)
  const x0 = Math.floor(Math.min(s0x, s1x, s2x)) - 1, y0 = Math.floor(Math.min(s0y, s1y, s2y)) - 1
  const x1 = Math.ceil(Math.max(s0x, s1x, s2x)) + 1, y1 = Math.ceil(Math.max(s0y, s1y, s2y)) + 1
  c.drawImage(img, x0, y0, x1 - x0, y1 - y0, x0, y0, x1 - x0, y1 - y0)
  c.restore()
}

let side: HTMLCanvasElement | null = null
function sideCanvas(main: CanvasRenderingContext2D): CanvasRenderingContext2D | null {
  const cv = (main as { canvas?: HTMLCanvasElement }).canvas
  if (!cv || typeof document === 'undefined' || typeof main.getTransform !== 'function') return null
  if (!side) side = document.createElement('canvas')
  // 布は柔らかいので、大きい画面では半分の細かさで描いて引き伸ばす (三角形ごとの切り抜きが重いため)。
  const k = cv.width > 1400 ? 0.5 : 1
  const sw = Math.ceil(cv.width * k), sh = Math.ceil(cv.height * k)
  if (side.width !== sw || side.height !== sh) { side.width = sw; side.height = sh }
  return side.getContext('2d')
}

// 重ねる布 (sides は2枚・奥→手前)。dh=高さの倍率・du=外へずらす量・lag=揺れの遅れ・a=濃さ・shade=影の色の濃さ。
type Layer = { dh: number; du: number; lag: number; a: number; shade: number; srcOff: number }
const LAYERS: Layer[] = [
  { dh: 1.1, du: 22, lag: 0.18, a: 0.62, shade: 0.5, srcOff: 0.37 },
  { dh: 1.0, du: 0, lag: 0, a: 0.95, shade: 0.22, srcOff: 0 },
]
// sides-tulle の3枚: 一枚布をまるごと貼り、背の高さ・外へのずれ・揺れの遅れを変えて重ねる (奥の2枚が手前の布の後ろから覗く)。
const TULLE_LAYERS: Layer[] = [
  { dh: 1.16, du: 46, lag: 0.3, a: 0.55, shade: 0.55, srcOff: 0.61 },
  { dh: 1.06, du: 20, lag: 0.15, a: 0.72, shade: 0.38, srcOff: 0.29 },
  { dh: 0.96, du: 0, lag: 0, a: 0.92, shade: 0.2, srcOff: 0 },
]

function drawLayer(ctx: CanvasRenderingContext2D, skin: Skin, g: Geom, L: Layer, floorY: number, pose: SidesPose, timeSec: number): void {
  const a = L.a * pose.alpha
  if (a <= 0.01) return
  const img = skin.img
  const rows = veilSidesMesh(g.w, g.H * L.dh, { ...pose, slide: pose.slide + L.du }, timeSec, g.seed + L.srcOff * 7, L.lag)
  const outerX = g.dir === 1 ? g.xStart : g.xEnd
  const X = (u: number): number => outerX + g.dir * u
  const Y = (y: number): number => floorY - y
  // 絵の取り出し: 横に長い絵は一部 (波ごと・層ごとにずらす)、slice 0 は絵をまるごと。左右反転は dir で (背中が外)。
  let rx = 0, rw = skin.w
  const rh = skin.h, ry = 0
  if (skin.slice > 0) {
    const sliceW = Math.min(rw, rh * skin.slice)
    rx = (rw - sliceW) * ((g.src + L.srcOff) % 1); rw = sliceW
  }
  if (!rw || !rh) return
  const SX = (j: number): number => rx + rw * (j / NS)
  const SY = (i: number): number => ry + rh * (1 - (1 - skin.cut) * i / NV)
  const sc = sideCanvas(ctx)
  const c = sc ?? ctx
  let box = { x: 0, y: 0, w: 0, h: 0 }, scale = 1
  if (sc) {
    // 脇の画面は波の囲みだけ消して使う (画面全体を毎回消すと重い)。
    const m0 = ctx.getTransform(), k = sc.canvas.width / ((ctx as { canvas: HTMLCanvasElement }).canvas.width || 1)
    const m = new DOMMatrix([m0.a * k, m0.b * k, m0.c * k, m0.d * k, m0.e * k, m0.f * k])
    scale = k
    let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
    for (const row of rows) for (const q of row) {
      const px = X(q.u), py = Y(q.y)
      const dx = m.a * px + m.c * py + m.e, dy = m.b * px + m.d * py + m.f
      x0 = Math.min(x0, dx); y0 = Math.min(y0, dy); x1 = Math.max(x1, dx); y1 = Math.max(y1, dy)
    }
    box = { x: Math.max(0, Math.floor(x0) - 4), y: Math.max(0, Math.floor(y0) - 4), w: 0, h: 0 }
    box.w = Math.min(sc.canvas.width, Math.ceil(x1) + 4) - box.x
    box.h = Math.min(sc.canvas.height, Math.ceil(y1) + 4) - box.y
    if (box.w <= 0 || box.h <= 0) return
    sc.setTransform(1, 0, 0, 1, 0, 0)
    sc.clearRect(box.x, box.y, box.w, box.h)
    sc.setTransform(m)
    sc.globalCompositeOperation = 'copy'
  } else {
    ctx.save()
    ctx.globalAlpha = a
  }
  for (let i = 0; i < NV; i += 1) {
    for (let j = 0; j < NS; j += 1) {
      const p00 = rows[i]![j]!, p01 = rows[i]![j + 1]!, p10 = rows[i + 1]![j]!, p11 = rows[i + 1]![j + 1]!
      texTri(c, img, SX(j), SY(i), SX(j + 1), SY(i), SX(j), SY(i + 1),
        X(p00.u), Y(p00.y), X(p01.u), Y(p01.y), X(p10.u), Y(p10.y))
      texTri(c, img, SX(j + 1), SY(i), SX(j + 1), SY(i + 1), SX(j), SY(i + 1),
        X(p01.u), Y(p01.y), X(p11.u), Y(p11.y), X(p10.u), Y(p10.y))
    }
  }
  if (!sc) { ctx.restore(); return }
  // 色: 床側を花嫁の菫に深く沈め、奥の層は全体を暗く (前の層と分けて見せる)。
  sc.globalCompositeOperation = 'source-atop'
  let top = Infinity
  for (const row of rows) for (const q of row) top = Math.min(top, Y(q.y))
  const gr = sc.createLinearGradient(0, floorY, 0, top)
  gr.addColorStop(0, `rgba(${swc('unparry', '92,64,150')},${(0.55 + L.shade * 0.4).toFixed(3)})`)
  gr.addColorStop(0.5, `rgba(${swc('unparry', '110,86,170')},${(L.shade * 0.7).toFixed(3)})`)
  gr.addColorStop(1, `rgba(150,130,210,${(L.shade * 0.4).toFixed(3)})`)
  sc.fillStyle = gr
  sc.fillRect(Math.min(X(-300), X(g.w + 300)), top - 20, 600 + g.w, floorY - top + 40)
  // 波頭の先は薄くぼかす (裂けた先が爪のように立たず、しぶきに見えるよう)。
  const tipRow = rows[NV]!, tip = tipRow[NS >> 1]!
  const tr = g.H * L.dh * 0.22
  const fade = sc.createRadialGradient(X(tip.u), Y(tip.y), 0, X(tip.u), Y(tip.y), tr)
  fade.addColorStop(0, 'rgba(0,0,0,0.85)')
  fade.addColorStop(1, 'rgba(0,0,0,0)')
  sc.globalCompositeOperation = 'destination-out'
  sc.fillStyle = fade
  sc.fillRect(X(tip.u) - tr, Y(tip.y) - tr, tr * 2, tr * 2)
  sc.globalCompositeOperation = 'source-over'
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.globalAlpha = a
  ctx.drawImage(sc.canvas, box.x, box.y, box.w, box.h, box.x / scale, box.y / scale, box.w / scale, box.h / scale)
  ctx.restore()
}

// 床の下からの菫の照り (弾けない合図・影の手と同じ色)。帯の中だけ。
function underGlow(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, a: number, h: number): void {
  if (a <= 0.01) return
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  ctx.beginPath()
  ctx.rect(g.xStart, floorY - h, g.w, h + 4)
  ctx.clip()
  ctx.translate(g.cx, floorY)
  ctx.scale((g.w / 2) / h, 1)
  const gr = ctx.createRadialGradient(0, 0, 0, 0, 0, h)
  gr.addColorStop(0, `rgba(${swc('unparry', '158,116,214')},${(0.5 * a).toFixed(3)})`)
  gr.addColorStop(0.6, `rgba(${swc('unparry', '150,110,204')},${(0.18 * a).toFixed(3)})`)
  gr.addColorStop(1, `rgba(${swc('unparry', '150,110,204')},0)`)
  ctx.fillStyle = gr
  ctx.fillRect(-h, -h, h * 2, h + 4)
  ctx.restore()
}

// ---------------------------------------------------------------- sides-wave (絵を貼らない影絵の波)

/** 波の背の高さ (幕の版より低い・人の背の1.5倍ほど)。 */
const WAVE_H = 0.74
/** 当たりの頭から、波頭の千切れが散り終わるまで (ms)。 */
const SPRAY_MS = 260

function bez(out: V2[], p0: V2, p1: V2, p2: V2, p3: V2, n: number): void {
  for (let i = 1; i <= n; i += 1) {
    const t = i / n, m = 1 - t
    out.push({
      u: m * m * m * p0.u + 3 * m * m * t * p1.u + 3 * m * t * t * p2.u + t * t * t * p3.u,
      y: m * m * m * p0.y + 3 * m * m * t * p1.y + 3 * m * t * t * p2.y + t * t * t * p3.y,
    })
  }
}

/**
 * 波の輪郭 (帯の座標・u = 外側の縁から内へ +・y = 床から上へ +)。
 * 外の床 (背の裾) → 背中 → 波頭 → 覆いかぶさる先 → 下へ巻き戻る腹 → 内へ窪んだ顔 → 床 (内側の縁) の順に一周する。
 * 内への届きは幕の版と同じ約束 (床際 GAP_FLOOR_PX 未満は帯の内側の縁まで・上は + OVERHANG_PX まで)。
 */
export function veilWaveOutline(w: number, H: number, pose: SidesPose, timeSec: number, seed: number, lag = 0): V2[] {
  const t = timeSec - lag
  const Hw = H * WAVE_H * pose.hs
  const c = clamp01((pose.curl - 0.4) / 2.7)          // 0 = 立ち上がり・1 = 崩れきった
  const uc = w * lerp(0.15, 0.45, c)                   // 波頭の根 (背の頂)
  const reach = (w + OVERHANG_PX - 4 - uc) * lerp(0.3, 1, c)
  const lipY = Hw * lerp(0.74, 0.4, c) + Math.sin(t * 3.1 + seed) * Hw * 0.025
  const crestY = Hw * lerp(1, 0.94, c)
  const back = -Hw * 0.62 * pose.spread
  const B0: V2 = { u: back, y: 0 }
  const C: V2 = { u: uc, y: crestY }
  const T: V2 = { u: uc + reach, y: lipY }
  const Q: V2 = { u: uc + reach * 0.18, y: lipY * 0.92 }
  const F: V2 = { u: w, y: 0 }
  const pts: V2[] = [B0]
  // 背中は根元が寝て頂の手前で急に立つ (下に凸)。波頭は前へ出てから先が下を向き、腹は奥へ巻き戻って空洞を作る。
  bez(pts, B0, { u: back * 0.4, y: Hw * 0.04 }, { u: uc - Hw * 0.3, y: crestY * 0.82 }, C, 14)
  bez(pts, C, { u: uc + reach * 0.6, y: crestY * 1.06 }, { u: T.u + Hw * 0.1, y: T.y + Hw * 0.3 }, T, 12)
  bez(pts, T, { u: T.u - Hw * 0.02, y: T.y - Hw * 0.2 }, { u: Q.u + Hw * 0.22, y: Q.y - Hw * 0.16 }, Q, 10)
  bez(pts, Q, { u: Q.u - Hw * 0.12, y: Q.y - Hw * 0.28 }, { u: w - Hw * 0.22, y: Hw * 0.04 }, F, 12)
  for (const q of pts) {
    q.y = Math.max(0, q.y)
    q.u = Math.min(q.u, q.y < GAP_FLOOR_PX ? w : w + OVERHANG_PX) - pose.slide
  }
  return pts
}

// 重ねる2枚 (奥→手前)。奥は同じ形を外へずらして遅らせた薄い影＝重なった布に見せる。中には線を描かない。
const WAVE_LAYERS = [
  { du: 40, dh: 1.1, lag: 0.25, a: 0.45, body: '150,140,206', dark: '86,64,146', rim: '200,196,240' },
  { du: 0, dh: 1, lag: 0, a: 0.82, body: '204,208,240', dark: '104,78,166', rim: '244,246,255' },
] as const

function waveShape(ctx: CanvasRenderingContext2D, pts: readonly V2[], X: (u: number) => number, Y: (y: number) => number, dy = 0): void {
  ctx.beginPath()
  pts.forEach((q, i) => (i === 0 ? ctx.moveTo(X(q.u), Y(q.y) + dy) : ctx.lineTo(X(q.u), Y(q.y) + dy)))
  ctx.closePath()
}

function drawCodeWave(ctx: CanvasRenderingContext2D, g: Geom, floorY: number, pose: SidesPose, timeSec: number, ms: number): void {
  const outerX = g.dir === 1 ? g.xStart : g.xEnd
  for (const L of WAVE_LAYERS) {
    const a = L.a * pose.alpha
    if (a <= 0.01) continue
    const pts = veilWaveOutline(g.w, g.H * L.dh, { ...pose, slide: pose.slide + L.du }, timeSec, g.seed, L.lag)
    const X = (u: number): number => outerX + g.dir * u
    const Y = (y: number): number => floorY - y
    const Hw = Math.max(...pts.map((q) => q.y))
    ctx.save()
    ctx.globalAlpha = a
    // 3色の段を塗りで作る (線は描かない)：
    //   上を向いた縁＝明るい銀白 (形を7px 下げた物の外)・内を向いた顔と波頭の下の空洞＝花嫁の菫 (形を外へずらした物の外)・残り＝胴。
    waveShape(ctx, pts, X, Y)
    ctx.clip()
    ctx.fillStyle = `rgb(${L.rim})`
    ctx.fill()
    waveShape(ctx, pts, X, Y, 7)
    ctx.fillStyle = `rgb(${swc('unparry', L.dark)})`
    ctx.fill()
    const out = Hw * 0.2
    waveShape(ctx, pts, (u) => X(u - out), Y, 7)
    ctx.fillStyle = `rgb(${L.body})`
    ctx.fill()
    ctx.restore()
    // 波頭の先の千切れ (2〜3枚の大きな舌・中の線にはしない)。
    const lip = pts.reduce((m, q) => (q.u > m.u ? q : m), pts[0]!)
    ctx.save()
    ctx.globalAlpha = a
    ctx.fillStyle = `rgb(${L.rim})`
    for (let k = 0; k < 3; k += 1) {
      const sway = Math.sin(timeSec * 4 + g.seed + k * 2.1) * 4
      const len = 16 + 8 * hash01(g.seed + k)
      const yy = lip.y - 4 - k * 9
      ctx.beginPath()
      ctx.moveTo(X(lip.u - 10 - k * 6), Y(yy + 6))
      ctx.lineTo(X(Math.min(lip.u + len * 0.4, g.w + OVERHANG_PX)), Y(yy - len + sway))
      ctx.lineTo(X(lip.u - 2 - k * 6), Y(yy - 4))
      ctx.closePath()
      ctx.fill()
    }
    ctx.restore()
  }
  // 崩れ落ちた瞬間、波頭から布の切れ端が散る (隙間の床際へは落とさない)。
  if (ms >= 0 && ms < SPRAY_MS && pose.alpha > 0.01) {
    const pts = veilWaveOutline(g.w, g.H, pose, timeSec, g.seed)
    const lip = pts.reduce((m, q) => (q.u > m.u ? q : m), pts[0]!)
    const f = ms / SPRAY_MS
    ctx.save()
    ctx.globalAlpha = (1 - f) * pose.alpha
    ctx.fillStyle = 'rgb(236,238,255)'
    for (let k = 0; k < 7; k += 1) {
      const r = hash01(g.seed * 3 + k)
      const vu = -30 + 70 * r, vy = 60 + 90 * hash01(g.seed + k * 5)
      const u = Math.min(lip.u + vu * f, g.w + OVERHANG_PX)
      const y = Math.max(GAP_FLOOR_PX + 4, lip.y + vy * f - 260 * f * f)
      const s = 4 + 5 * hash01(k + g.seed * 7)
      const X = outerX + g.dir * u, Y = floorY - y
      ctx.beginPath()
      ctx.moveTo(X - s, Y); ctx.lineTo(X, Y - s * 0.7); ctx.lineTo(X + s, Y + s * 0.3); ctx.lineTo(X, Y + s * 0.6)
      ctx.closePath()
      ctx.fill()
    }
    ctx.restore()
  }
}

function ready(kind: VeilSidesKind): boolean { return kind === 'sides-wave' || !!skinOf(kind) }

function drawWave(ctx: CanvasRenderingContext2D, kind: VeilSidesKind, g: Geom, floorY: number, pose: SidesPose, timeSec: number, ms = -1): boolean {
  if (kind === 'sides-wave') { drawCodeWave(ctx, g, floorY, pose, timeSec, ms); return true }
  const skin = skinOf(kind)
  if (!skin) return false
  for (const L of skin.layers) drawLayer(ctx, skin, g, L, floorY, pose, timeSec)
  return true
}

/** 予告 (telegraph)。帯の外から背の高い波が寄せ、波頭が内へ傾いていく。絵が読めていなければ false。 */
export function drawVeilSidesTelegraph(ctx: CanvasRenderingContext2D, kind: VeilSidesKind, hz: ActiveHit, timeSec: number, floorY: number, dir: 1 | -1): boolean {
  dirs.set(hz.id, dir)
  const g = geomOf(hz, dir)
  const p = clamp01(hz.elapsedMs / hz.telegraphMs)
  const pulse = p >= 0.75 ? 1 + 0.18 * Math.sin(timeSec * 14) : 1
  if (!ready(kind)) return false
  underGlow(ctx, g, floorY, (0.2 + 0.6 * smooth01(0.3, 1, p)) * pulse, 30 + 30 * p)
  return drawWave(ctx, kind, g, floorY, veilSidesPose(hz.elapsedMs - hz.telegraphMs, hz.activeMs, hz.telegraphMs), timeSec)
}

/** 当たり (active)。絵が読めていなければ false。 */
export function drawVeilSidesActive(ctx: CanvasRenderingContext2D, kind: VeilSidesKind, hz: ActiveHit, timeSec: number, floorY: number, dir: 1 | -1): boolean {
  if (!ready(kind)) return false
  const ms = hz.elapsedMs - hz.telegraphMs
  const d = dirs.get(hz.id) ?? dir
  dirs.set(hz.id, d)
  const g = geomOf(hz, d)
  remember(kind, g, hz, timeSec - ms / 1000)
  drawAt(ctx, kind, g, floorY, ms, hz.activeMs, timeSec)
  return true
}

function drawAt(ctx: CanvasRenderingContext2D, kind: VeilSidesKind, g: Geom, floorY: number, ms: number, activeMs: number, timeSec: number): void {
  // 照り: 崩れ落ちで強く、当たりの終わりで 100ms のうちに引く (危険が終わったと分かる)。
  const glowA = (ms < 130 ? 1.25 - ms / 600 : 1) * (1 - smooth01(activeMs - 20, activeMs + 100, ms))
  underGlow(ctx, g, floorY, glowA, 70)
  drawWave(ctx, kind, g, floorY, veilSidesPose(ms, activeMs), timeSec, ms)
}

// ---------------------------------------------------------------- 当たりの後の残り

type Trace = { kind: VeilSidesKind; g: Geom; startSec: number; activeMs: number }
const traces = new Map<number, Trace>()
function remember(kind: VeilSidesKind, g: Geom, hz: ActiveHit, startSec: number): void {
  if (!traces.has(hz.id)) traces.set(hz.id, { kind, g, startSec, activeMs: hz.activeMs })
}

/** 当たりが消えた後の残り (崩れ落ちて床に広がりながら薄れる)。render.ts の drawHazards の最後で呼ぶ。描くだけ。 */
export function drawVeilSidesLinger(ctx: CanvasRenderingContext2D, timeSec: number, hits: readonly ActiveHit[], floorY: number): void {
  if (traces.size === 0) return
  for (const [id, tr] of traces) {
    const ms = (timeSec - tr.startSec) * 1000
    if (ms < 0 || ms > tr.activeMs + FALL_MS) { traces.delete(id); dirs.delete(id); continue }
    if (hits.some((h) => h.id === id)) continue   // まだ当たりの中 = 本体側で描いている
    drawAt(ctx, tr.kind, tr.g, floorY, ms, tr.activeMs, timeSec)
  }
}
